<div class="modal-overlay" (click)="onClose()">
  <div class="modal-container" (click)="$event.stopPropagation()">
    <div class="modal-header">
      <h2>Settings</h2>
      <button class="modal-close" (click)="onClose()">&times;</button>
    </div>

    <div class="modal-body">
      <!-- Error/Success Messages -->
      <div *ngIf="error" class="message message-error">{{ error }}</div>
      <div *ngIf="successMessage" class="message message-success">{{ successMessage }}</div>

      <!-- Loading State -->
      <div *ngIf="loading" class="loading-spinner">
        <div class="spinner"></div>
        <p>Loading...</p>
      </div>

      <!-- Teams List View -->
      <div *ngIf="currentView === 'teams' && !loading" class="view-container">
        <div class="view-header">
          <h3>Your Teams</h3>
          <div class="view-actions">
            <button class="btn btn-secondary" (click)="openSupportDialog()">
              Contact Support
            </button>
            <button class="btn btn-primary" (click)="showCreateTeamForm()">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 3V13M3 8H13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
              New Team
            </button>
          </div>
        </div>

        <div class="profile-panel">
          <div class="profile-avatar-preview">
            <img
              *ngIf="currentUser?.avatarUrl; else profileInitialAvatar"
              [src]="currentUser?.avatarUrl"
              alt="Profile picture"
            />
            <ng-template #profileInitialAvatar>
              <span>{{ profileInitial }}</span>
            </ng-template>
          </div>

          <div class="profile-meta">
            <h4>Profile</h4>
            <p>Public name: <strong>{{ profileDisplayName }}</strong></p>
            <p class="profile-meta-hint">
              Set a nickname for public leaderboards. Leave empty to auto-generate a random SimRacer name.
            </p>
            <div class="profile-nickname-row">
              <input
                type="text"
                class="profile-nickname-input"
                maxlength="32"
                [ngModel]="nicknameDraft"
                (ngModelChange)="onNicknameDraftChanged($event)"
                placeholder="Nickname"
                autocomplete="nickname"
                [disabled]="nicknameSaving"
              />
              <button
                class="btn btn-secondary"
                (click)="saveNickname()"
                [disabled]="nicknameSaving"
              >
                {{ nicknameSaving ? 'Saving...' : 'Save Nickname' }}
              </button>
            </div>
            <p class="profile-meta-hint">
              Avatar upload: PNG, JPG, WEBP (validated for type, size, and dimensions).
            </p>
          </div>

          <div class="profile-actions">
            <input
              #avatarFileInput
              type="file"
              class="avatar-file-input"
              accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
              (change)="onAvatarFileSelected($event)"
            />
            <button class="btn btn-secondary" (click)="openAvatarPicker(avatarFileInput)" [disabled]="avatarSaving">
              {{ currentUser?.avatarUrl ? 'Change Photo' : 'Upload Photo' }}
            </button>
            <button
              *ngIf="currentUser?.avatarUrl"
              class="btn btn-secondary btn-danger-muted"
              (click)="removeProfileAvatar()"
              [disabled]="avatarSaving"
            >
              Remove
            </button>
          </div>
        </div>

        <div *ngIf="pendingInvitations.length > 0" class="invitations-notice" (click)="showInvitations()">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z" stroke="currentColor" stroke-width="2"/>
            <path d="M10 6V10M10 14H10.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
          <span>You have {{ pendingInvitations.length }} pending invitation{{ pendingInvitations.length > 1 ? 's' : '' }}</span>
          <svg class="chevron" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>

        <div class="teams-list">
          <div *ngIf="teams.length === 0" class="empty-state">
            <p>You don't have any teams yet</p>
            <button class="btn btn-secondary" (click)="showCreateTeamForm()">Create Your First Team</button>
          </div>

          <div *ngFor="let team of teams" class="team-card" (click)="showTeamDetails(team)">
            <div class="team-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="team-info">
              <h4>{{ team.name }}</h4>
              <p *ngIf="team.description">{{ team.description }}</p>
              <span class="team-date">Created {{ team.createdAt | date: 'mediumDate' }}</span>
            </div>
            <svg class="chevron" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
        </div>
      </div>

      <!-- Create Team View -->
      <div *ngIf="currentView === 'create-team' && !loading" class="view-container">
        <div class="view-header">
          <button class="btn-back" (click)="backToTeams()">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Back
          </button>
          <h3>Create New Team</h3>
        </div>

        <form (ngSubmit)="createTeam()" class="form-container">
          <div class="form-group">
            <label for="teamName">Team Name *</label>
            <input
              id="teamName"
              type="text"
              [(ngModel)]="newTeamName"
              name="teamName"
              placeholder="Enter team name"
              class="input-field"
              required
              autofocus
            />
          </div>

          <div class="form-group">
            <label for="teamDescription">Description (Optional)</label>
            <textarea
              id="teamDescription"
              [(ngModel)]="newTeamDescription"
              name="teamDescription"
              placeholder="Describe your team"
              class="input-field textarea"
              rows="3"
            ></textarea>
          </div>

          <button type="submit" class="btn btn-primary btn-block" [disabled]="!newTeamName.trim()">
            Create Team
          </button>
        </form>
      </div>

      <!-- Team Details View -->
      <div *ngIf="currentView === 'team-details' && selectedTeam && !loading" class="view-container">
        <div class="view-header">
          <button class="btn-back" (click)="backToTeams()">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Back
          </button>
          <h3>{{ selectedTeam.team.name }}</h3>
        </div>

        <div class="team-details">
          <p *ngIf="selectedTeam.team.description" class="team-description">
            {{ selectedTeam.team.description }}
          </p>
          <p>
            Team management now lives in the dedicated Team Admin screen.
          </p>
          <p>
            Your role:
            <span class="badge" [ngClass]="getRoleBadgeClass(selectedTeam.userRole)">
              {{ selectedTeam.userRole }}
            </span>
          </p>
          <button
            class="btn btn-primary"
            [routerLink]="['/teams', selectedTeam.team.id, 'admin']"
            (click)="onClose()"
          >
            Open Team Admin
          </button>
        </div>
      </div>

      <!-- Invitations View -->
      <div *ngIf="currentView === 'invitations' && !loading" class="view-container">
        <div class="view-header">
          <button class="btn-back" (click)="backToTeams()">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Back
          </button>
          <h3>Pending Invitations</h3>
        </div>

        <div class="invitations-list">
          <div *ngIf="pendingInvitations.length === 0" class="empty-state">
            <p>No pending invitations</p>
          </div>

          <div *ngFor="let invitation of pendingInvitations" class="invitation-card">
            <div class="invitation-info">
              <h4>{{ invitation.teamName }}</h4>
              <p>Invited by {{ invitation.invitedByName }}</p>
              <span class="invitation-date">{{ invitation.createdAt | date: 'medium' }}</span>
            </div>
            <button class="btn btn-success" (click)="acceptInvitation(invitation)">
              Accept
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div *ngIf="avatarCropOpen" class="avatar-crop-overlay" (click)="closeAvatarCropper(); $event.stopPropagation()">
    <div class="avatar-crop-dialog" (click)="$event.stopPropagation()">
      <h3>Crop Profile Picture</h3>
      <p>Drag to position. Use zoom for tighter framing.</p>

      <div class="avatar-crop-frame" (pointerdown)="onAvatarCropPointerDown($event)">
        <img
          *ngIf="avatarSourceDataUrl"
          [src]="avatarSourceDataUrl"
          alt="Crop preview"
          [style.width.px]="avatarRenderedWidthPx"
          [style.height.px]="avatarRenderedHeightPx"
          [style.transform]="'translate(' + avatarTranslateXPx + 'px, ' + avatarTranslateYPx + 'px)'"
          draggable="false"
        />
      </div>

      <div class="avatar-zoom-control">
        <label for="avatarZoom">Zoom</label>
        <input
          id="avatarZoom"
          type="range"
          min="1"
          [max]="maxAvatarZoom"
          step="0.01"
          [(ngModel)]="avatarZoom"
          (input)="onAvatarZoomInput()"
        />
      </div>

      <div class="avatar-crop-actions">
        <button class="btn btn-secondary" (click)="closeAvatarCropper()" [disabled]="avatarSaving">
          Cancel
        </button>
        <button class="btn btn-primary" (click)="saveCroppedAvatar()" [disabled]="avatarSaving">
          {{ avatarSaving ? 'Saving...' : 'Save Picture' }}
        </button>
      </div>
    </div>
  </div>

  <div *ngIf="showSupportDialog" class="support-dialog-overlay" (click)="closeSupportDialog(); $event.stopPropagation()">
    <div class="support-dialog" (click)="$event.stopPropagation()">
      <h3>Contact Support</h3>
      <p>Describe the issue and we will review it.</p>

      <div *ngIf="supportError" class="message message-error">{{ supportError }}</div>
      <div *ngIf="supportSuccessMessage" class="message message-success">{{ supportSuccessMessage }}</div>

      <textarea
        [(ngModel)]="supportMessage"
        class="input-field textarea support-textarea"
        placeholder="Type your message..."
        rows="6"
      ></textarea>

      <div class="support-dialog-actions">
        <button class="btn btn-secondary" (click)="closeSupportDialog()" [disabled]="supportSubmitting">
          Cancel
        </button>
        <button class="btn btn-primary" (click)="submitSupportMessage()" [disabled]="supportSubmitting || !supportMessage.trim()">
          {{ supportSubmitting ? 'Sending...' : 'Send Message' }}
        </button>
      </div>
    </div>
  </div>
</div>
